import React, { useEffect, useState } from 'react';
import { Grid, Paper, Typography, Box } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell } from 'recharts';
import { getCompanies, getProjects } from '../services/api';
import { useTranslation } from 'react-i18next';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const StatCard = ({ title, value, subtitle }) => (
  <Paper sx={{ p: 2, height: '100%' }}>
    <Typography variant="h6" gutterBottom>{title}</Typography>
    <Typography variant="h4">{value}</Typography>
    <Typography variant="body2" color="textSecondary">{subtitle}</Typography>
  </Paper>
);

function Dashboard() {
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState({
    companies: 0,
    projects: 0,
    activeProjects: 0,
    projectsByStatus: [],
    projectsByMonth: [],
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [companiesRes, projectsRes] = await Promise.all([
          getCompanies(),
          getProjects(),
        ]);

        const projects = projectsRes.data;
        const projectsByStatus = [
          { name: t('status.notStarted'), value: projects.filter(p => p.status === 'NOT_STARTED').length },
          { name: t('status.inProgress'), value: projects.filter(p => p.status === 'IN_PROGRESS').length },
          { name: t('status.done'), value: projects.filter(p => p.status === 'DONE').length },
        ];

        // Group projects by month
        const projectsByMonth = Array.from({ length: 12 }, (_, i) => {
          const month = new Date(2024, i, 1).toLocaleString(i18n.language, { month: 'short' });
          return {
            month,
            count: projects.filter(p => new Date(p.startDate).getMonth() === i).length,
          };
        });

        setStats({
          companies: companiesRes.data.length,
          projects: projects.length,
          activeProjects: projects.filter(p => p.status === 'IN_PROGRESS').length,
          projectsByStatus,
          projectsByMonth,
        });
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      }
    };

    fetchStats();
  }, [i18n.language, t]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>{t('dashboard.title')}</Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <StatCard
            title={t('dashboard.totalCompanies')}
            value={stats.companies}
            subtitle={t('dashboard.registeredCompanies')}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard
            title={t('dashboard.totalProjects')}
            value={stats.projects}
            subtitle={t('dashboard.allProjects')}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard
            title={t('dashboard.activeProjects')}
            value={stats.activeProjects}
            subtitle={t('dashboard.projectsInProgress')}
          />
        </Grid>

        {/* Project Status Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>{t('dashboard.projectStatusDistribution')}</Typography>
            <PieChart width={400} height={300}>
              <Pie
                data={stats.projectsByStatus}
                cx={200}
                cy={150}
                labelLine={false}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {stats.projectsByStatus.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </Paper>
        </Grid>

        {/* Projects by Month */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>{t('dashboard.projectsByMonth')}</Typography>
            <BarChart width={400} height={300} data={stats.projectsByMonth}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#8884d8" name={t('dashboard.projects')} />
            </BarChart>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard; 